import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { signOutSupabaseSession } from '@cropconnect/fdtm-module';

import { msalInstance } from '../services/auth/msalInstance';
import { API_CONFIG } from '../services/apiConfig';
import { msalConfig } from '../services/auth/msalConfig';
import { getDeploymentTarget } from '../intergrations/fdtmAndContracting/environmentMapper';

interface AuthState {
  navigatedUnauthenticated: boolean;
  needsToLogIn: boolean;
  loading: boolean;
  getAuthToken: () => Promise<string | undefined>;
  logout: () => void;
  safeLogoutWithAcquireTokenSilent: () => void;
  setLoading: (loading: boolean) => void;
}

const authResultObject: {
  isAuthenticating: boolean;
  authTokenPromise: Promise<string | undefined> | undefined;
} = { isAuthenticating: false, authTokenPromise: undefined };
const acquireTokenSilent = async (account: AccountInfo) => {
  return msalInstance.acquireTokenSilent({
    scopes: [API_CONFIG.API_AUTH_SCOPE!],
    authority: msalInstance.getConfiguration().auth.authority,
    account: account,
    prompt: 'none',
  });
};
export const useAuthStore = create<AuthState>()(
  devtools((set) => ({
    navigatedUnauthenticated: false,
    needsToLogIn: false,
    loading: false,
    getAuthToken: async () => {
      if (authResultObject.isAuthenticating) {
        return await authResultObject.authTokenPromise;
      }
      authResultObject.isAuthenticating = true;
      authResultObject.authTokenPromise = new Promise<string | undefined>(
        (resolve, reject) => {
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length !== 1) {
            console.debug(`Found ${accounts.length} active accounts.`);
            console.debug(JSON.stringify(accounts));
            console.debug(
              `Had it been active account it would be:${JSON.stringify(msalInstance.getActiveAccount())}`
            );
            const loginRequest = {
              scopes: [API_CONFIG.API_AUTH_SCOPE!],
              authority: msalInstance.getConfiguration().auth.authority,
              prompt: 'login',
            };
            msalInstance
              .acquireTokenPopup(loginRequest)
              .then((pr) => resolve(pr.accessToken))
              .catch((e) => {
                const account = msalInstance.getAccount({ isHomeTenant: true });
                msalInstance.logoutRedirect({
                  account: account,
                  idTokenHint: account?.idToken,
                  logoutHint: account?.homeAccountId,
                });
                console.error(`Error getting token in popup`);
                console.error(e);
                resolve(undefined);
              })
              .finally(() => {
                authResultObject.isAuthenticating = false;
              });
          } else {
            acquireTokenSilent(accounts[0])
              .then((r) => {
                authResultObject.isAuthenticating = false;
                resolve(r.accessToken);
              })
              .catch((e) => {
                if (e instanceof InteractionRequiredAuthError) {
                  set({ needsToLogIn: true });
                  const loginRequest = {
                    scopes: [API_CONFIG.API_AUTH_SCOPE!],
                    authority: msalInstance.getConfiguration().auth.authority,
                    account: accounts[0],
                    prompt: 'login',
                  };
                  msalInstance
                    .acquireTokenPopup(loginRequest)
                    .then((pr) => resolve(pr.accessToken))
                    .catch((error) => {
                      const account = msalInstance.getAccount({
                        isHomeTenant: true,
                      });
                      msalInstance.logoutRedirect({
                        account: account,
                        idTokenHint: account?.idToken,
                        logoutHint: account?.homeAccountId,
                      });
                      console.error(`Error getting token in popup`);
                      console.error(error);
                      resolve(undefined);
                    })
                    .finally(() => {
                      authResultObject.isAuthenticating = false;
                    });
                } else {
                  authResultObject.isAuthenticating = false;
                  reject(e);
                }
              });
          }
        }
      );
      return authResultObject.authTokenPromise;
    },
    logout: async () => {
      signOutSupabaseSession(getDeploymentTarget()).then(() => {
        console.log(
          'CropConnect: Supabase session signed out. Deployment target:',
          getDeploymentTarget()
        );
      });
      set({ needsToLogIn: true, loading: true });
      const account = msalInstance.getAccount({ isHomeTenant: true });
      await msalInstance.logoutRedirect({
        account: account,
        idTokenHint: account?.idToken,
        logoutHint: account?.homeAccountId,
        postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
      });
      set({ loading: false });
    },
    safeLogoutWithAcquireTokenSilent: async () => {
      try {
        await signOutSupabaseSession(getDeploymentTarget());
        console.log(
          'CropConnect: Supabase session signed out. Deployment target:',
          getDeploymentTarget()
        );
      } catch (e) {
        console.warn('Supabase sign-out failed', e);
      }
      set({ needsToLogIn: true, loading: true });
      const account = msalInstance.getAccount({ isHomeTenant: true });
      const silentResult = await msalInstance.acquireTokenSilent({
        scopes: ['openid'],
        account,
      });
      const freshIdToken = silentResult.idToken;

      await msalInstance.logoutRedirect({
        account: account,
        idTokenHint: freshIdToken,
        logoutHint: account?.homeAccountId,
        postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
      });
      set({ loading: false });
    },
    setLoading: (loading: boolean) => set({ loading }),
  }))
);
